/**
 * Nearest point calculation for polyline entities.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DrawingModuleType } from "../../DrawingSingleton";
import { deleteObj } from "../../core/delete";
import { SegType } from "../../core/constants";

/**
 * Get the closest point on a polyline to a given point.
 *
 * Since OdDbPolyline.getClosestPointTo() may not be implemented in JS bindings,
 * this iterates through each segment and finds the closest point using
 * OdGeLineSeg2d.closestPointTo() for line segments and OdGeCircArc2d.closestPointTo() for arcs.
 *
 * @param module The ODA drawing module
 * @param polyline The polyline entity
 * @param givenPoint The point to find the closest point to
 * @returns The closest point on the polyline, or null if not found
 */
export function getClosestPointToPolyline(
  module: DrawingModuleType,
  polyline: DrawingModule.OdDbPolyline,
  givenPoint: DrawingModule.OdGePoint3d,
): DrawingModule.OdGePoint3d | null {
  const numVerts = polyline.numVerts();

  if (numVerts === 0) return null;

  // For closed polylines, numSegs = numVerts. For open, numSegs = numVerts - 1.
  const isClosed = polyline.isClosed();
  const numSegs = isClosed ? numVerts : numVerts - 1;

  if (numSegs === 0) return null;

  let closestPoint: DrawingModule.OdGePoint3d | null = null;
  let minDistSq = Infinity;

  for (let i = 0; i < numSegs; i++) {
    const segType = polyline.segType(i);
    let candidatePoint: DrawingModule.OdGePoint3d | null = null;

    if (segType === SegType.kLine) {
      // Handle line segment (polyline uses 2D geometry)
      const lineSeg = new module.OdGeLineSeg2d();
      polyline.getLineSegAt(i, lineSeg); // Populates lineSeg by reference

      const givenPoint2d = new module.OdGePoint2d(givenPoint.x, givenPoint.y);
      const tol = new module.OdGeTol(1e-10);
      const closestPt2d = lineSeg.closestPointTo(givenPoint2d, tol);
      candidatePoint = new module.OdGePoint3d(closestPt2d.x, closestPt2d.y, 0);
      deleteObj(closestPt2d);
      deleteObj(givenPoint2d);
      deleteObj(tol);
      deleteObj(lineSeg);
    } else if (segType === SegType.kArc) {
      // Handle arc segment (polyline uses 2D geometry)
      const arcSeg = new module.OdGeCircArc2d();
      polyline.getArcSegAt(i, arcSeg); // Populates arcSeg by reference

      const givenPoint2d = new module.OdGePoint2d(givenPoint.x, givenPoint.y);
      const tol = new module.OdGeTol(1e-10);
      const closestPt2d = arcSeg.closestPointTo(givenPoint2d, tol);
      candidatePoint = new module.OdGePoint3d(closestPt2d.x, closestPt2d.y, 0);
      deleteObj(closestPt2d);
      deleteObj(givenPoint2d);
      deleteObj(tol);
      deleteObj(arcSeg);
    } else {
      // Skip kCoincident, kPoint, kEmpty segments
      continue;
    }

    if (candidatePoint) {
      const dx = candidatePoint.x - givenPoint.x;
      const dy = candidatePoint.y - givenPoint.y;
      const dz = candidatePoint.z - givenPoint.z;
      const distSq = dx * dx + dy * dy + dz * dz;

      if (distSq < minDistSq) {
        minDistSq = distSq;
        // Clean up previous closest point if any
        if (closestPoint) {
          deleteObj(closestPoint);
        }
        closestPoint = candidatePoint;
      } else {
        deleteObj(candidatePoint);
      }
    }
  }

  return closestPoint;
}
